import { useEffect, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
} from "../../components/ui";
import {
  TextField,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ClioService,
  ClioTasksInfo,
  CustomFields,
  MatterPreviewResponse,
} from "../../api";
import {
  ClioStaffTypeSelect,
  ClioUserSelect,
} from "../../components/shared/ClioSelects";

const MySwal = withReactContent(Swal);

const TABLE_COLUMNS = [
  { id: "id", label: "ID", sortable: false },
  { id: "description", label: "Description", sortable: false },
];

export default function ClioTaskAssignees() {
  const [allMatters, setAllMatters] = useState<MatterPreviewResponse[]>([]);
  const [filteredMatters, setFilteredMatters] = useState<MatterPreviewResponse[]>([]);
  const [, setAllTasks] = useState<ClioTasksInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedUserTypes, setSelectedUserTypes] = useState<string | null>(null);
  const [selectedMatters, setSelectedMatters] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    ClioService.getApiClioMatters("").then((matters) => {
      const sortedMatters = matters.sort(
        (m1, m2) =>
          m1.description?.localeCompare(m2.description ?? "") ?? -1
      );
      setAllMatters(sortedMatters);
      setFilteredMatters(sortedMatters);
    });

    ClioService.getApiClioTaskData().then((tasks) => {
      const sortedTasks = tasks.sort(
        (t1, t2) => t1.name?.localeCompare(t2.name ?? "") ?? -1
      );
      setAllTasks(sortedTasks);
    });
  }, []);

  // Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMatters(allMatters);
      return;
    }
    const lower = searchTerm.toLowerCase();
    setFilteredMatters(
      allMatters.filter(
        (m) =>
          m.description?.toLowerCase().includes(lower)
      )
    );
    setPage(0);
  }, [searchTerm, allMatters]);

  // Toggle selection
  const toggleMatter = (id: number) => {
    setSelectedMatters((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const visibleIds = filteredMatters
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((m) => m.id!);
      setSelectedMatters((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      const visibleIds = filteredMatters
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((m) => m.id);
      setSelectedMatters((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleAssignTask = async () => {
    if (!selectedUser || selectedMatters.length === 0 || !selectedUserTypes) {
      MySwal.fire({
        icon: "warning",
        title: "Select at least one matter and one task type.",
      });
      return;
    }

    try {
      // Example: call your backend to assign a task to multiple matters
      await ClioService.postApiUpdateTaskAssignee({
        matterIds: selectedMatters,
        assigneeType: selectedUserTypes as CustomFields,
        assigneeId: selectedUser
      });

      MySwal.fire({
        icon: "success",
        title: `${selectedMatters.length} matter(s) linked to task successfully.`,
      });
      setSelectedMatters([]);
      setSelectedUserTypes(null);
      setSelectedUser(null);
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Error assigning task to matters.",
      });
    }
  };

  const pageRows = filteredMatters.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="pt-3">
      <PageHeader
        title="Update assignees"
        subtitle="Change task assignees across Clio matters."
      />
      {/* Controls */}
      <div className="row">
        <div className="col-3">
          <TextField
            placeholder="Search matters..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
        </div>
        
        <div className="col-3">
          <ClioUserSelect
            label="Select User"
            fullWidth
            required
            includeAll={false}
            valueMode="id"
            removeDisabled
            value={selectedUser ?? undefined}
            onChange={(userId) => setSelectedUser(userId as number)}
          />
        </div>

        <div className="col-3">
          <ClioStaffTypeSelect
            value={selectedUserTypes}
            onChange={setSelectedUserTypes}
            required
          />
        </div>
        <div className="col-3">
          <AppButton
            variant="contained"
            color="primary"
            disabled={!selectedUserTypes || selectedMatters.length === 0 || !selectedUser}
            onClick={handleAssignTask}
          >
            Set
          </AppButton>
        </div>
      </div>

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber={false}
        leadingHeadCell={
          <AppTableHeadCell padding="checkbox">
            <Checkbox
              indeterminate={
                selectedMatters.length > 0 &&
                selectedMatters.length < pageRows.length
              }
              checked={
                pageRows.length > 0 &&
                pageRows.every((m) => selectedMatters.includes(m.id!))
              }
              onChange={(e) => toggleAll(e.target.checked)}
            />
          </AppTableHeadCell>
        }
        extraColumnCount={1}
        isEmpty={pageRows.length === 0}
        emptyTitle="No matters found"
        pagination={{
          count: filteredMatters.length,
          page,
          rowsPerPage,
          rowsPerPageOptions: [10, 20, 50],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {pageRows.map((matter) => (
          <AppTableRow key={matter.id}>
            <AppTableCell padding="checkbox">
              <Checkbox
                checked={selectedMatters.includes(matter.id!)}
                onChange={() => toggleMatter(matter.id!)}
              />
            </AppTableCell>
            <AppTableCell>{matter.id}</AppTableCell>
            <AppTableCell
              sx={{
                maxWidth: 250,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {matter.description}
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>
    </div>
  );
}
